import { datadogLogs } from '@datadog/browser-logs';
import { datadogRum } from '@datadog/browser-rum';
import config from '../config';

// Only for client-side use
class DataDogInstance {
  private static instance: DataDogInstance | null = null;
  private static consentGranted = false;
  private _datadogLogs: typeof datadogLogs;
  private _datadogRum: typeof datadogRum;
  private monInit: boolean = false;

  private constructor() {
    this._datadogLogs = datadogLogs;
    this._datadogRum = datadogRum;
  }

  public static getInstance(): DataDogInstance {
    if (typeof window === 'undefined') {
      throw new Error(
        'DataDogInstance is only available in browser environments'
      );
    }

    if (!DataDogInstance.instance) {
      DataDogInstance.instance = new DataDogInstance();
    }
    return DataDogInstance.instance;
  }

  static enableMonitoring(): void {
    DataDogInstance.consentGranted = true;
    DataDogInstance.getInstance().createDataDogConnection();
  }

  static isConsentGranted(): boolean {
    return DataDogInstance.consentGranted;
  }

  static disableMonitoring(): void {
    DataDogInstance.consentGranted = false;
    datadogRum.stopSession();
    datadogLogs.logger.setHandler('silent');
  }

  private createDataDogConnection(): void {
    if (this.monInit) {
      return;
    }

    if (process.env.NODE_ENV === 'development' && !config.MONITOR_ENV) {
      return;
    }

    this.monInit = true;

    const monitorEnvs = new Set(['prod', 'uat', 'dev']);
    let monitorEnv = config.MONITOR_ENV ?? process.env.NEXT_PUBLIC_MONITOR_ENV;

    if (!monitorEnv) {
      const cenv =
        config.ENVIRONMENT ??
        process.env.NEXT_PUBLIC_ENVIRONMENT ??
        process.env.NODE_ENV;
      monitorEnv = cenv ? (cenv.startsWith('p') ? 'prod' : 'dev') : 'dev';
    }

    // Helper detectors to filter noisy bot/phantom errors
    // https://github.com/DataDog/browser-sdk/issues/2715
    const isCefSharpUA =
      typeof navigator !== 'undefined' && /CefSharp/i.test(navigator.userAgent);

    const getMessageFromPayload = (payload: unknown): string => {
      if (payload == null) return '';
      if (typeof payload === 'string') return payload;
      if (typeof payload === 'object') {
        const obj = payload as {
          message?: unknown;
          error?: { message?: unknown; stack?: unknown };
        };
        const parts: string[] = [];
        if (typeof obj.message === 'string') parts.push(obj.message);
        if (obj.error) {
          if (typeof obj.error.message === 'string')
            parts.push(obj.error.message);
          if (typeof obj.error.stack === 'string') parts.push(obj.error.stack);
        }
        return parts.join(' ');
      }
      return '';
    };

    // Narrow a minimal RUM error event shape
    type RumErrorEventSubset = {
      type: 'error';
      error?: { message?: string; stack?: string };
      message?: string;
    };

    const isRumErrorEvent = (ev: unknown): ev is RumErrorEventSubset => {
      if (typeof ev !== 'object' || ev === null) return false;
      const obj = ev as { type?: unknown };
      return obj.type === 'error';
    };

    const isPhantomObjectNotFound = (payload: unknown) => {
      const msg = getMessageFromPayload(payload);
      return /Object Not Found Matching Id:\s*\d+\s*,\s*MethodName:update\s*,\s*ParamCount:\s*\d+/i.test(
        msg
      );
    };

    this._datadogLogs.init({
      clientToken: config.DATADOG_CLIENT_TOKEN ?? 'ADD_CLIENT_TOKEN',
      site: 'datadoghq.com',
      ...(config.DATADOG_PROXY_URL ? { proxy: config.DATADOG_PROXY_URL } : {}),
      service: 'coign-site',
      env: monitorEnv,
      version: config.VERSION ?? process.env.NEXT_PUBLIC_VERSION ?? '1.0.0',
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      forwardConsoleLogs: 'all',
      silentMultipleInit: true,
      beforeSend: (log) => {
        // Drop noisy source/network errors and bot-generated phantom errors
        if (
          log.error &&
          (log.error.origin == 'source' ||
            (log.error.kind == 'TypeError' && log.error.origin == 'network'))
        ) {
          return false;
        }
        // Drop XHR/Fetch network errors (auto-tracked by Datadog)
        if (log.error && log.error.origin === 'network') {
          return false;
        }
        if (isCefSharpUA || isPhantomObjectNotFound(log)) {
          return false;
        }
        return true;
      },
    });

    if (monitorEnvs.has(monitorEnv)) {
      this._datadogRum.init({
        applicationId:
          config.DATADOG_APPLICATION_ID ??
          '3d9faa82-d497-46f2-8b00-5b4aabdaaea5',
        clientToken: config.DATADOG_CLIENT_TOKEN ?? 'ADD_CLIENT_TOKEN',
        site: 'datadoghq.com',
        ...(config.DATADOG_PROXY_URL
          ? { proxy: config.DATADOG_PROXY_URL }
          : {}),
        service: 'coign-site',
        env: monitorEnv,
        version: config.VERSION ?? process.env.NEXT_PUBLIC_VERSION ?? '1.0.0',
        sessionSampleRate: 100,
        sessionReplaySampleRate: 0,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        silentMultipleInit: true,
        allowedTracingUrls: [
          'https://www.coign.com',
          'https://coign.com',
          /https:\/\/.*\.coign\.com/,
        ],
        beforeSend: (event) => {
          // Filter bot noise for RUM events as well
          // Only applicable to error events
          // See: https://github.com/DataDog/browser-sdk/issues/2715
          if (isRumErrorEvent(event)) {
            const msg = getMessageFromPayload(event);
            if (
              isCefSharpUA ||
              /Object Not Found Matching Id:\s*\d+\s*,\s*MethodName:update\s*,\s*ParamCount:\s*\d+/i.test(
                msg
              )
            ) {
              return false;
            }
          }
          return true;
        },
      });
      this._datadogRum.startSessionReplayRecording();
    } else {
      this._datadogLogs.logger.setHandler('console');
    }

    const rid = this.getSessionIdentifier();
    if (rid) {
      this._datadogLogs.setGlobalContextProperty('requestid', rid);
    }
  }

  public logDebug(msg: string, msgContext?: object, err?: Error): void {
    if (!this.monInit) return;
    this._datadogLogs.logger.debug(msg, msgContext, err);
  }

  public logInfo(msg: string, msgContext?: object, err?: Error): void {
    if (!this.monInit) return;
    this._datadogLogs.logger.info(msg, msgContext, err);
  }

  public logWarn(msg: string, msgContext?: object, err?: Error): void {
    if (!this.monInit) return;
    this._datadogLogs.logger.warn(msg, msgContext, err);
  }

  public logError(msg: string, msgContext?: object, err?: Error): void {
    if (!this.monInit) return;
    this._datadogLogs.logger.error(msg, msgContext, err);
  }

  public logCritical(msg: string, msgContext?: object, err?: Error): void {
    if (!this.monInit) return;
    this._datadogLogs.logger.critical(msg, msgContext, err);
  }

  private getSessionIdentifier(): string | undefined {
    return this._datadogLogs
      .getInternalContext()
      ?.session_id?.replace(/-/g, '');
  }
}

export default DataDogInstance;

export const datadogController = {
  enable() {
    DataDogInstance.enableMonitoring();
  },
  disable() {
    if (DataDogInstance.isConsentGranted()) {
      DataDogInstance.disableMonitoring();
    }
  },
};
